/**
 * WONE-612: method chooser — rendered when ``learnerAuthMethods``
 * returns 2+ methods after the HIDDEN_METHODS filter.
 *
 * Mirrors mobile (PR #820/#828): heading is "Sign in" or "Create an
 * account" based on userState, no subtitle. All method rows use a
 * single visual treatment (white pill + icon + label) — password (when
 * offered) on top, then OTP, then "Or" divider, then SSO providers in the order the resolver
 * returned them.
 *
 * Mobile parity: src/app/(auth)/method-chooser/index.tsx.
 */

import React, { useEffect, useState } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "lib/i18n/use-translation";

import {
  AuthLayout,
  Divider,
  EnvelopeIcon,
  Heading,
  LegalFooter,
  SSOButton,
} from "apps/user/common/components/auth-layout";

import PasswordIcon from "Assets/icon/icon_password.svg";
import GoogleIcon from "Assets/googleicon.svg";
import MicrosoftIcon from "Assets/microsofticon.svg";
import OktaIcon from "Assets/icon/single-sign-on/okta@3x.png";

import { trackEvent } from "utility/mixpanel";

import {
  AuthUserState,
  isSsoProvider,
  navigateToMethod,
  SsoProvider,
} from "./navigate-to-method";

import styles from "./method-chooser.module.scss";

type AuthMethod = { id: string; name: string };

type LocationState = {
  email: string;
  userState: AuthUserState;
  methods: AuthMethod[];
  returnTo?: string;
};

const SSO_ICONS: Record<SsoProvider, string> = {
  "google-oauth2": GoogleIcon,
  "azuread-oauth2": MicrosoftIcon,
  okta: OktaIcon,
};

// Maps to `auth` catalog keys rather than literal copy so labels localise
// with the rest of the slice.
const SSO_LABEL_KEYS: Record<SsoProvider, string> = {
  "google-oauth2": "methodChooser.continueWithGoogle",
  "azuread-oauth2": "methodChooser.continueWithMicrosoft",
  okta: "methodChooser.continueWithOkta",
};

const CREDENTIAL_LABEL_KEYS: Record<string, string> = {
  email_otp: "methodChooser.sendMeACode",
  "email-and-pwd": "methodChooser.continueWithPassword",
};

const MethodChooser: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation(["auth", "common"]);
  const state = (location.state as LocationState | null) || null;

  // Per-button loading lock so the user can't double-tap into two
  // simultaneous SSO redirects. SSO calls window.location.assign(...)
  // so a successful click never returns; the loading state matters
  // most for the moment between click and redirect dispatch.
  const [activeMethodId, setActiveMethodId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Defensive: if a user deep-links straight to /auth/methods without
  // going through /email-entry, route state will be null. Send them
  // back to the entry screen rather than rendering an empty page.
  useEffect(() => {
    if (!state) {
      navigate("/email-entry", { replace: true });
    }
  }, [state, navigate]);

  // SSO redirects via window.location.assign and never returns, so we
  // leave the clicked button spinning on the success path. But pressing
  // Back restores this page from the browser's back-forward cache with
  // React state frozen — the button would stay stuck spinning and lock
  // out every other method until a hard refresh. ``pageshow`` with
  // ``persisted`` is the bfcache-restore signal; reset the lock there so
  // the page is usable again.
  useEffect(() => {
    const handlePageShow = (event: PageTransitionEvent): void => {
      if (event.persisted) {
        setActiveMethodId(null);
        setError(null);
      }
    };
    window.addEventListener("pageshow", handlePageShow);
    return () => window.removeEventListener("pageshow", handlePageShow);
  }, []);

  if (!state) {
    return <Navigate to="/email-entry" replace />;
  }

  const { email, userState, methods, returnTo } = state;

  // Bucket the methods into OTP / SSO so we can render the mobile-parity
  // order: OTP → divider → SSO. Preserves server-side order within each
  // bucket; password renders alongside OTP. Unrecognised ids are dropped.
  const otpMethod = methods.find((m) => m.id === "email_otp") ?? null;
  const pwdMethod = methods.find((m) => m.id === "email-and-pwd") ?? null;
  const ssoMethods = methods.filter((m) => isSsoProvider(m.id));

  const dispatch = async (methodId: string): Promise<void> => {
    setError(null);
    setActiveMethodId(methodId);
    trackEvent("Auth Method Selected", {
      Method: methodId,
      "User State": userState,
    });
    if (isSsoProvider(methodId)) {
      // Of the WONE-712 SSO family (Started/Completed/Cancelled/Error)
      // web deliberately omits ``Auth SSO Cancelled``: the flow is a
      // full-page redirect to the IdP, so a user who abandons there
      // never re-enters the app — there is no cancel signal to observe
      // (unlike mobile's in-app auth sheet). Noted on WONE-713.
      trackEvent("Auth SSO Started", { Provider: methodId });
    }
    try {
      await navigateToMethod({
        methodId,
        email,
        userState,
        navigate,
        returnTo,
      });
    } catch {
      setActiveMethodId(null);
      setError("Something went wrong. Please try again.");
      trackEvent("Auth Server Error Shown", {
        Screen: "method_chooser",
        "Error Code": "method_dispatch_failed",
      });
      if (isSsoProvider(methodId)) {
        trackEvent("Auth SSO Error", {
          Provider: methodId,
          Code: "dispatch_failed",
        });
      }
    }
  };

  const title =
    userState === "EXISTING"
      ? t("methodChooser.titleSignIn")
      : t("methodChooser.titleSignUp");
  const otpLabelKey =
    (otpMethod && CREDENTIAL_LABEL_KEYS[otpMethod.id]) ||
    "methodChooser.sendMeACode";
  const otpLabel = t(otpLabelKey);

  return (
    <AuthLayout variant="form" onBack={() => navigate("/email-entry")}>
      <div className={styles["body"]}>
        <Heading>{title}</Heading>

        <div className={styles["method-stack"]}>
          {pwdMethod && (
            <SSOButton
              label={t(CREDENTIAL_LABEL_KEYS[pwdMethod.id])}
              icon={PasswordIcon}
              onClick={() => dispatch(pwdMethod.id)}
              loading={activeMethodId === pwdMethod.id}
              disabled={
                activeMethodId !== null && activeMethodId !== pwdMethod.id
              }
            />
          )}

          {otpMethod && (
            <SSOButton
              label={otpLabel}
              icon={<EnvelopeIcon size={20} />}
              onClick={() => dispatch(otpMethod.id)}
              loading={activeMethodId === otpMethod.id}
              disabled={
                activeMethodId !== null && activeMethodId !== otpMethod.id
              }
            />
          )}

          {(otpMethod || pwdMethod) && ssoMethods.length > 0 && (
            <Divider label={t("common:divider.or")} />
          )}

          {ssoMethods.map((method) => {
            const id = method.id as SsoProvider;
            return (
              <SSOButton
                key={id}
                label={t(SSO_LABEL_KEYS[id])}
                icon={SSO_ICONS[id]}
                onClick={() => dispatch(id)}
                loading={activeMethodId === id}
                disabled={activeMethodId !== null && activeMethodId !== id}
              />
            );
          })}
        </div>

        {error && (
          <p className={styles["email-summary"]} role="alert">
            {error}
          </p>
        )}

        <LegalFooter />
      </div>
    </AuthLayout>
  );
};

export default MethodChooser;
